/**
 * Storezone — Auth Context
 *
 * Provides the authenticated user's profile and tenant memberships 
 * throughout the app.
 *
 * Wrap the app with <AuthProvider> to make auth state available
 * everywhere via the useAuth() hook.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { apiClient, AUTH_EXPIRED_EVENT, TokenManager } from '@/lib/api'
import type { AuthMeResponse, TenantMembershipSummary, UserProfile } from '@/types'

// ============================================================
// Context Shape
// ============================================================

export interface AuthContextValue {
  /** True if we have an access token (session) */
  isAuthenticated: boolean

  /** Storezone profile from the backend */
  profile: UserProfile | null

  /** All tenant memberships for this user */
  memberships: TenantMembershipSummary[]

  /** True if the user has platform-wide superadmin access */
  isSuperadmin: boolean

  /** True while the initial auth state is being resolved */
  loading: boolean

  /** Sign in with email + password via backend */
  signIn: (email: string, password: string) => Promise<AuthMeResponse | undefined | void>

  /** Sign out and clear all state */
  signOut: () => Promise<void>

  /** Refresh the profile and memberships from the backend */
  refreshProfile: () => Promise<void>
}

// ============================================================
// Context
// ============================================================

export const AuthContext = createContext<AuthContextValue | null>(null)

// ============================================================
// Provider
// ============================================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!TokenManager.getAccessToken())
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [memberships, setMemberships] = useState<TenantMembershipSummary[]>([])
  const [isSuperadmin, setIsSuperadmin] = useState(false)
  
  // We only start loading if we have a token that we need to verify
  const [loading, setLoading] = useState<boolean>(!!TokenManager.getAccessToken())

  // Prevent concurrent fetchProfile calls
  const fetchingRef = useRef(false)

  // --------------------------------------------------------
  // Fetch profile from backend
  // --------------------------------------------------------
  const fetchProfile = useCallback(async () => {
    if (fetchingRef.current) return
    fetchingRef.current = true

    try {
      const me = await apiClient.get<AuthMeResponse>('/api/v1/auth/me')
      setProfile(me.user)
      setMemberships(me.memberships)
      setIsSuperadmin(me.is_superadmin)
      setIsAuthenticated(true)
      return me
    } catch {
      // Profile fetch failed (could be 401 or network error)
      // The API client will dispatch AUTH_EXPIRED_EVENT on 401s
    } finally {
      fetchingRef.current = false
    }
  }, [])

  // --------------------------------------------------------
  // On mount — restore session
  // --------------------------------------------------------
  useEffect(() => {
    if (TokenManager.getAccessToken()) {
      fetchProfile().finally(() => {
        setLoading(false)
      })
    } else {
      setLoading(false)
    }
  }, [fetchProfile])

  // --------------------------------------------------------
  // Sign In
  // --------------------------------------------------------
  const signIn = useCallback(async (email: string, password: string) => {
    const res = await apiClient.post<{ access_token: string; refresh_token: string }>('/api/v1/auth/login', {
      email,
      password
    })
    
    TokenManager.setTokens(res.access_token, res.refresh_token)
    setIsAuthenticated(true)
    return await fetchProfile()
  }, [fetchProfile])

  // --------------------------------------------------------
  // Sign Out
  // --------------------------------------------------------
  const signOut = useCallback(async () => {
    // Attempt to tell the backend to revoke (fire and forget)
    try {
      if (TokenManager.getAccessToken()) {
        await apiClient.post('/api/v1/auth/logout')
      }
    } catch {
      // Ignore errors during logout
    }

    TokenManager.clearTokens()
    setIsAuthenticated(false)
    setProfile(null)
    setMemberships([])
    setIsSuperadmin(false)
  }, [])

  // --------------------------------------------------------
  // Listen for Global Auth Expiration
  // --------------------------------------------------------
  useEffect(() => {
    const handleAuthExpired = () => {
      TokenManager.clearTokens()
      setIsAuthenticated(false)
      setProfile(null)
      setMemberships([])
      setIsSuperadmin(false)
    }
    
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired)
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired)
    }
  }, [])

  // --------------------------------------------------------
  // Refresh Profile
  // --------------------------------------------------------
  const refreshProfile = useCallback(async () => {
    await fetchProfile()
  }, [fetchProfile])

  const value: AuthContextValue = {
    isAuthenticated,
    profile,
    memberships,
    isSuperadmin,
    loading,
    signIn,
    signOut,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
